import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Grid,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import axios from "axios";
import { api } from "config";

interface PaymentPerson {
  id: number
  personName: string
  surname: string
  cf: string
  address: string
  city: string
  cap: string
  code: string
}

interface Props {
  setStep: React.Dispatch<React.SetStateAction<number>>
  setSelectedPersonId: (id: number) => void
}

const SelectPaymentPersonPage: React.FC<Props> = ({
  setStep,
  setSelectedPersonId,
}) => {
  const [persons, setPersons] = React.useState<PaymentPerson[]>([])
  const [selectedPerson, setSelectedPerson] = React.useState<string>("")
  const toast = useToast()

  const [openModal, setOpenModal] = React.useState(false)

  const [form, setForm] = React.useState({
    personName: "",
    surname: "",
    cf: "",
    address: "",
    city: "",
    cap: "",
    code: "",
  })

  const handleChange = (key: string, value: string) => {
    setForm((p) => ({ ...p, [key]: value }))
  }

  const loadPersons = async () => {
    try {
      const res = await axios.get(`${api}/payment-people`, {
        withCredentials: true,
      })

      setPersons(res.data)

     if (res.data.length > 0 && !selectedPerson) {
       setSelectedPerson(String(res.data[0].id))
       setSelectedPersonId(res.data[0].id)
     }
    } catch (err) {
      toast({
        title: "Failed to load persons",
        position: "bottom-right",
        status: "error",
      })
    }
  }

  React.useEffect(() => {
    loadPersons()
  }, [])

  const savePerson = async () => {
    if (!form.personName || !form.surname || !form.cf) {
      toast({
        title: "Name, surname and fiscal code are required",
        position: "bottom-right",
        status: "warning",
      })
      return
    }

    try {
      const res = await axios.post(`${api}/payment-people`, form, {
        withCredentials: true,
      })

      await loadPersons()
      setSelectedPerson(String(res.data.id))
      setSelectedPersonId(res.data.id)
      setOpenModal(false)
    } catch (err) {
      toast({
        title: "Failed to save person",
        position: "bottom-right",
        status: "error",
      })
    }
  }

  return (
    <Box>
      <Text fontWeight="bold" mb={3}>
        Payment කරන කෙනා තෝරන්න
      </Text>

      <Select
        placeholder="Select payment person"
        value={selectedPerson}
        onFocus={loadPersons}
        onChange={(e) => {
          setSelectedPerson(e.target.value)
          setSelectedPersonId(Number(e.target.value))
        }}
      >
        {persons.map((p) => (
          <option key={p.id} value={p.id}>
            {p.personName} {p.surname}
          </option>
        ))}
      </Select>

      <Flex mt={3} justify="space-between">
        <Button onClick={() => setOpenModal(true)}>+ Add New Person</Button>

        <Button
          colorScheme="green"
          isDisabled={!selectedPerson}
          onClick={() => setStep(3)}
        >
          Continue
        </Button>
      </Flex>

      <Modal isOpen={openModal} onClose={() => setOpenModal(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Payment Person</ModalHeader>

          <ModalBody>
            <Grid templateColumns="1fr 1fr" gap={3}>
              <Input
                placeholder="Nome"
                value={form.personName}
                onChange={(e) => handleChange("personName", e.target.value)}
              />

              <Input
                placeholder="Cognome"
                value={form.surname}
                onChange={(e) => handleChange("surname", e.target.value)}
              />
              <Input
                placeholder="Codice Fiscale"
                value={form.cf}
                onChange={(e) => handleChange("cf", e.target.value)}
              />

              <Input
                gridColumn="1 / span 2"
                placeholder="Via"
                value={form.address}
                onChange={(e) => handleChange("address", e.target.value)}
              />

              <Input
                placeholder="Numero"
                value={form.code}
                onChange={(e) => handleChange("code", e.target.value)}
              />

              <Input
                placeholder="Città"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />

              <Input
                placeholder="Postal code"
                value={form.cap}
                onChange={(e) => handleChange("cap", e.target.value)}
              />
            </Grid>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={() => setOpenModal(false)}>
              Cancel
            </Button>

            <Button colorScheme="blue" onClick={savePerson}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default SelectPaymentPersonPage;
